import React, { useEffect } from 'react';
import { AppBar, Box, Container, CssBaseline, Toolbar, Typography, createTheme, ThemeProvider, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import BuildIcon from '@mui/icons-material/Build';

const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#00bcd4',
        },
        secondary: {
            main: '#ff4081',
        },
        background: {
            default: '#121212',
            paper: '#1d1d1d',
        },
        text: {
            primary: '#ffffff',
            secondary: '#b0bec5',
        },
    },
    typography: {
        h4: {
            fontWeight: 700,
        },
        h6: {
            fontWeight: 700,
        },
    },
});

const MaintenanceContent = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #424242 30%, #616161 90%)',
    padding: theme.spacing(8, 0, 6),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'calc(100vh - 64px)',
    textAlign: 'center',
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(4, 0, 3),
        minHeight: 'auto',
    },
}));

function Maintenance() {
    useEffect(() => {
        // Check maintenance mode status periodically
        const checkMaintenanceStatus = async () => {
            try {
                const response = await fetch('/version.json', { cache: 'no-store' });
                
                if (!response.ok) {
                    console.error('Failed to fetch maintenance status');
                    return;
                }
                
                const data = await response.json();
                const { maintenanceMode } = data;
                
                if (!maintenanceMode) {
                    // Redirect to home page
                    window.location.href = '/';
                }
            } catch (error) {
                console.error('Failed to check maintenance status:', error);
            }
        };

        // Check immediately
        checkMaintenanceStatus();

        // Then check every 30 seconds
        const interval = setInterval(checkMaintenanceStatus, 30000);

        return () => clearInterval(interval);
    }, []);

    const handleRefresh = () => {
        window.location.reload();
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Toolbar sx={{ flexWrap: 'wrap', px: { xs: 1, sm: 2 } }}>
                    <Typography variant="h6" color="inherit" noWrap sx={{ flexGrow: 1, fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        WorldsHighStreet
                    </Typography>
                </Toolbar>
            </AppBar>

            <MaintenanceContent>
                <Container maxWidth="sm">
                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                        <BuildIcon sx={{ fontSize: { xs: 80, sm: 120 }, color: 'primary.main' }} />
                    </Box>
                    <Typography component="h1" variant="h2" align="center" gutterBottom sx={{ fontSize: { xs: '2rem', sm: '3rem' }, fontWeight: 800 }}>
                        Under Maintenance
                    </Typography>
                    <Typography variant="h5" align="center" paragraph sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' }, color: 'text.secondary', mb: 4 }}>
                        We're currently performing scheduled maintenance to improve your experience.
                    </Typography>
                    <Typography variant="body1" align="center" paragraph sx={{ fontSize: { xs: '1rem', sm: '1.15rem' }, color: 'text.secondary', mb: 4 }}>
                        Please check back soon. We'll be back up and running shortly.
                    </Typography>
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleRefresh}
                            sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' } }}
                        >
                            Refresh Page
                        </Button>
                    </Box>
                </Container>
            </MaintenanceContent>
        </ThemeProvider>
    );
}

export default Maintenance;
